import { Check, Clock, Loader2, Pause, Play, RefreshCw, Tags, Users } from 'lucide-react'
import { type DragEvent, useEffect, useMemo, useState } from 'react'
import {
  type AudioGroup,
  type AudioTrackBindingItem,
  type AudioTrackBindingKind,
  type AudioTrackBindingListResult,
  type AudioTrackBindingState
} from '../../../api'
import type { Label } from '../../../api/labels'
import { Button, DataTableElement, DataTableSurface, EmptyState, IconButton, SearchField, SegmentedControl, TablePagination, TableSortButton } from '../../../components/ui'
import { formatBpm } from '../audioFormatting'
import { AudioAssignmentsMobileList } from '../AudioAssignmentsMobileList'
import { labelCategoryLabels, labelCategoryOptions } from '../../labels/labelDisplay'
import { labelChipClassName, labelMoreClassName } from '../../labels/labelUiStyles'
import { formatDuration } from '../../visuals/formatDuration'
import styles from './AudioAssignments.module.css'

type AudioAssignmentsProps = {
  groups: AudioGroup[]
  labels: Label[]
  onLoadBindings: (filters: {
    kind: AudioTrackBindingKind
    targetId: string
    state: AudioTrackBindingState
    page: number
    pageSize: number
    search?: string | undefined
    sortBy: 'created_at' | 'duration' | 'title'
    sortDirection: 'asc' | 'desc'
  }) => Promise<AudioTrackBindingListResult>
  onToggleBinding: (input: {
    kind: AudioTrackBindingKind
    targetId: string
    trackId: string
    assigned: boolean
  }) => Promise<void>
  onError: (message: string) => void
  loadingTrackId: string | null
  onPlayTrack: (track: AudioTrackBindingItem) => void
  playingTrackId: string | null
}

type DraggedAssignmentTarget = {
  kind: AudioTrackBindingKind
  name: string
  targetId: string
}

const pageSizeOptions = [25, 50, 100]
const searchDebounceMs = 300
const maxVisibleAssignmentLabels = 6
const assignmentDragDataType = 'application/x-mavolyra-audio-assignment'
const cx = (...classes: Array<string | false | null | undefined>): string => classes.filter(Boolean).join(' ')

const getTargetName = (
  kind: AudioTrackBindingKind,
  targetId: string,
  labels: Label[],
  groups: AudioGroup[]
): string => {
  if (kind === 'label') {
    return labels.find((label) => label.id === targetId)?.name ?? 'Selected label'
  }

  return groups.find((group) => group.id === targetId)?.name ?? 'Selected group'
}

export const AudioAssignments = ({
  groups,
  labels,
  onError,
  onLoadBindings,
  onPlayTrack,
  onToggleBinding,
  loadingTrackId,
  playingTrackId
}: AudioAssignmentsProps) => {
  const activeLabels = useMemo(() => labels.filter((label) => label.status === 'active'), [labels])
  const firstLabelId = activeLabels[0]?.id ?? ''
  const firstGroupId = groups[0]?.id ?? ''
  const [kind, setKind] = useState<AudioTrackBindingKind>('label')
  const [labelTargetId, setLabelTargetId] = useState(firstLabelId)
  const [groupTargetId, setGroupTargetId] = useState(firstGroupId)
  const [state, setState] = useState<AudioTrackBindingState>('all')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState<string | undefined>(undefined)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [sortBy, setSortBy] = useState<'created_at' | 'duration' | 'title'>('created_at')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
  const [items, setItems] = useState<AudioTrackBindingItem[]>([])
  const [total, setTotal] = useState(0)
  const [assignedTotal, setAssignedTotal] = useState(0)
  const [ungroupedTotal, setUngroupedTotal] = useState(0)
  const [unassignedTotal, setUnassignedTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [draggedTarget, setDraggedTarget] = useState<DraggedAssignmentTarget | null>(null)
  const [dragOverTrackId, setDragOverTrackId] = useState<string | null>(null)
  const [selectedTrackId, setSelectedTrackId] = useState<string | null>(null)
  const [pendingTrackIds, setPendingTrackIds] = useState<string[]>([])

  const targetId = kind === 'label' ? labelTargetId : groupTargetId
  const targetName = getTargetName(kind, targetId, activeLabels, groups)
  const selectedTrack = selectedTrackId ? items.find((item) => item.id === selectedTrackId) ?? null : null
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const firstItemNumber = total === 0 ? 0 : (page - 1) * pageSize + 1
  const lastItemNumber = Math.min(page * pageSize, total)

  useEffect(() => {
    if (!labelTargetId && firstLabelId) {
      setLabelTargetId(firstLabelId)
    }
  }, [firstLabelId, labelTargetId])

  useEffect(() => {
    if (!groupTargetId && firstGroupId) {
      setGroupTargetId(firstGroupId)
    }
  }, [firstGroupId, groupTargetId])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setSearch(searchInput.trim() ? searchInput.trim() : undefined)
      setPage(1)
    }, searchDebounceMs)

    return () => window.clearTimeout(timeoutId)
  }, [searchInput])

  const loadBindings = async (): Promise<void> => {
    if (!targetId) {
      setItems([])
      setTotal(0)
      setAssignedTotal(0)
      setUngroupedTotal(0)
      setUnassignedTotal(0)
      return
    }

    setLoading(true)
    try {
      const result = await onLoadBindings({
        kind,
        targetId,
        state,
        page,
        pageSize,
        search,
        sortBy,
        sortDirection
      })

      setItems(result.items)
      setTotal(result.total)
      setAssignedTotal(result.assignedTotal)
      setUngroupedTotal(result.ungroupedTotal)
      setUnassignedTotal(result.unassignedTotal)
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Unable to load assignments.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadBindings()
  }, [kind, targetId, state, page, pageSize, search, sortBy, sortDirection])

  useEffect(() => {
    if (total > 0 && page > totalPages) {
      setPage(totalPages)
    }
  }, [page, total, totalPages])

  const selectKind = (nextKind: AudioTrackBindingKind): void => {
    setKind(nextKind)
    setPage(1)
    setState('all')
  }

  const selectTarget = (nextTargetId: string): void => {
    if (kind === 'label') {
      setLabelTargetId(nextTargetId)
    } else {
      setGroupTargetId(nextTargetId)
    }
    setPage(1)
  }

  const selectMobileTarget = (nextKind: AudioTrackBindingKind, nextTargetId: string): void => {
    const kindChanged = nextKind !== kind

    setKind(nextKind)
    if (nextKind === 'label') {
      setLabelTargetId(nextTargetId)
    } else {
      setGroupTargetId(nextTargetId)
    }
    if (kindChanged) {
      setState('all')
    }
    setPage(1)
  }

  const setStateFilter = (nextState: AudioTrackBindingState): void => {
    setState(nextState)
    setPage(1)
  }

  const toggleSort = (nextSortBy: 'created_at' | 'duration' | 'title'): void => {
    setSortDirection(sortBy === nextSortBy && sortDirection === 'asc' ? 'desc' : 'asc')
    setSortBy(nextSortBy)
    setPage(1)
  }

  const isTargetAssignedToTrack = (
    track: AudioTrackBindingItem,
    draggedAssignmentTarget: DraggedAssignmentTarget
  ): boolean => {
    if (draggedAssignmentTarget.kind === 'label') {
      return track.labels.some((label) => label.id === draggedAssignmentTarget.targetId)
    }

    return track.groups.some((group) => group.id === draggedAssignmentTarget.targetId)
  }

  const isSelectedTrackLinkedToTarget = (targetKind: AudioTrackBindingKind, nextTargetId: string): boolean => {
    if (!selectedTrack) {
      return false
    }

    if (targetKind === 'label') {
      return selectedTrack.labels.some((label) => label.id === nextTargetId)
    }

    return selectedTrack.groups.some((group) => group.id === nextTargetId)
  }

  const getTargetItemClassName = (
    targetKind: AudioTrackBindingKind,
    nextTargetId: string,
    active: boolean
  ): string => {
    return cx(
      styles.targetItem,
      active ? styles.targetItemActive : '',
      isSelectedTrackLinkedToTarget(targetKind, nextTargetId) ? styles.targetItemLinked : ''
    )
  }

  const startDraggingTarget = (
    event: DragEvent<HTMLButtonElement>,
    nextDraggedTarget: DraggedAssignmentTarget
  ): void => {
    setDraggedTarget(nextDraggedTarget)
    event.dataTransfer.effectAllowed = 'copy'
    event.dataTransfer.setData(assignmentDragDataType, JSON.stringify(nextDraggedTarget))
    event.dataTransfer.setData('text/plain', nextDraggedTarget.name)
  }

  const getDroppedTarget = (event: DragEvent<HTMLTableRowElement>): DraggedAssignmentTarget | null => {
    if (draggedTarget) {
      return draggedTarget
    }

    const serializedTarget = event.dataTransfer.getData(assignmentDragDataType)

    if (!serializedTarget) {
      return null
    }

    try {
      const parsedTarget = JSON.parse(serializedTarget) as Partial<DraggedAssignmentTarget>

      if (
        (parsedTarget.kind === 'label' || parsedTarget.kind === 'group')
        && typeof parsedTarget.targetId === 'string'
        && typeof parsedTarget.name === 'string'
      ) {
        return {
          kind: parsedTarget.kind,
          name: parsedTarget.name,
          targetId: parsedTarget.targetId
        }
      }
    } catch {
      return null
    }

    return null
  }

  const addDraggedAssignment = async (
    track: AudioTrackBindingItem,
    droppedTarget: DraggedAssignmentTarget
  ): Promise<void> => {
    if (pendingTrackIds.includes(track.id) || isTargetAssignedToTrack(track, droppedTarget)) {
      return
    }

    setPendingTrackIds((current) => [...current, track.id])

    try {
      await onToggleBinding({
        kind: droppedTarget.kind,
        targetId: droppedTarget.targetId,
        trackId: track.id,
        assigned: true
      })
      await loadBindings()
    } catch (error) {
      onError(error instanceof Error ? error.message : `Unable to add ${droppedTarget.name} to ${track.title}.`)
    } finally {
      setPendingTrackIds((current) => current.filter((id) => id !== track.id))
    }
  }

  const dropAssignmentOnTrack = (event: DragEvent<HTMLTableRowElement>, track: AudioTrackBindingItem): void => {
    event.preventDefault()
    const droppedTarget = getDroppedTarget(event)

    setSelectedTrackId(track.id)
    setDragOverTrackId(null)
    setDraggedTarget(null)

    if (!droppedTarget) {
      return
    }

    void addDraggedAssignment(track, droppedTarget)
  }

  const toggleBinding = async (track: AudioTrackBindingItem): Promise<void> => {
    if (!targetId || pendingTrackIds.includes(track.id)) {
      return
    }

    const nextAssigned = !track.assigned
    const previousItems = items
    const previousAssignedTotal = assignedTotal
    const previousUngroupedTotal = ungroupedTotal
    const previousUnassignedTotal = unassignedTotal
    const trackHasOtherGroups = track.groups.some((group) => group.id !== targetId)

    setPendingTrackIds((current) => [...current, track.id])
    setItems((current) => current.map((item) => item.id === track.id ? { ...item, assigned: nextAssigned } : item))
    setAssignedTotal((current) => current + (nextAssigned ? 1 : -1))
    setUngroupedTotal((current) => {
      if (kind !== 'group') {
        return current
      }

      if (nextAssigned && track.groups.length === 0) {
        return current - 1
      }

      if (!nextAssigned && !trackHasOtherGroups) {
        return current + 1
      }

      return current
    })
    setUnassignedTotal((current) => current + (nextAssigned ? -1 : 1))

    try {
      await onToggleBinding({
        kind,
        targetId,
        trackId: track.id,
        assigned: nextAssigned
      })
      await loadBindings()
    } catch (error) {
      setItems(previousItems)
      setAssignedTotal(previousAssignedTotal)
      setUngroupedTotal(previousUngroupedTotal)
      setUnassignedTotal(previousUnassignedTotal)
      onError(error instanceof Error ? error.message : 'Unable to update assignment.')
    } finally {
      setPendingTrackIds((current) => current.filter((id) => id !== track.id))
    }
  }

  const renderTargetList = () => {
    if (kind === 'group') {
      return (
        <div className={styles.targetList}>
          {groups.map((group) => (
            <button
              className={getTargetItemClassName('group', group.id, groupTargetId === group.id)}
              draggable
              key={group.id}
              onDragEnd={() => setDraggedTarget(null)}
              onDragStart={(event) => startDraggingTarget(event, {
                kind: 'group',
                name: group.name,
                targetId: group.id
              })}
              onClick={() => selectTarget(group.id)}
              title={`Drag ${group.name} onto a track to add it`}
              type="button"
            >
              <span className={styles.targetItemName}>{group.name}</span>
              <span className={styles.targetItemCount}>{group.track_count} tracks</span>
            </button>
          ))}
          {groups.length === 0 && <EmptyState message="Create an audio group first." />}
        </div>
      )
    }

    return (
      <div className={styles.targetList}>
        {labelCategoryOptions.map((category) => {
          const categoryLabels = activeLabels.filter((label) => label.category === category)

          if (categoryLabels.length === 0) {
            return null
          }

          return (
            <div className={styles.targetCategory} key={category}>
              <span>{labelCategoryLabels[category]}</span>
              {categoryLabels.map((label) => (
                <button
                  className={getTargetItemClassName('label', label.id, labelTargetId === label.id)}
                  draggable
                  key={label.id}
                  onDragEnd={() => setDraggedTarget(null)}
                  onDragStart={(event) => startDraggingTarget(event, {
                    kind: 'label',
                    name: label.name,
                    targetId: label.id
                  })}
                  onClick={() => selectTarget(label.id)}
                  title={`Drag ${label.name} onto a track to add it`}
                  type="button"
                >
                  <span className={styles.targetItemName}>{label.name}</span>
                  <span className={styles.targetItemCount}>{label.track_count} tracks</span>
                </button>
              ))}
            </div>
          )
        })}
        {activeLabels.length === 0 && <EmptyState message="Create active labels first." />}
      </div>
    )
  }

  const pagination = (
    <TablePagination
      ariaLabel="Audio assignment pagination"
      currentPage={page}
      onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
      onPrevious={() => setPage((current) => Math.max(1, current - 1))}
      pageSize={{
        onChange: (nextPageSize) => {
          setPageSize(nextPageSize)
          setPage(1)
        },
        options: pageSizeOptions,
        value: pageSize
      }}
      summary={total === 0 ? 'No tracks' : `${firstItemNumber}-${lastItemNumber} of ${total}`}
      totalPages={totalPages}
    />
  )

  return (
    <>
    <div className={styles.desktopLayout}>
      <aside className={styles.targetPanel}>
        <SegmentedControl
          ariaLabel="Assignment mode"
          items={[
            { icon: <Tags size={15} />, label: 'Tags', value: 'label' },
            { icon: <Users size={15} />, label: 'Groups', value: 'group' }
          ]}
          layout="equal"
          onChange={selectKind}
          value={kind}
          variant="panel"
        />
        {renderTargetList()}
      </aside>

      <DataTableSurface className={styles.tracksPanel}>
        <div className={styles.summary}>
          <div className={styles.headerTools}>
            <SearchField
              className={styles.searchField}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search tracks..."
              value={searchInput}
            />
            <Button disabled={loading || !targetId} onClick={() => void loadBindings()}>
              <RefreshCw size={16} /> Refresh
            </Button>
          </div>
          <SegmentedControl
            ariaLabel="Assignment state"
            items={[
              { count: assignedTotal + unassignedTotal, label: 'All', value: 'all' },
              { count: assignedTotal, label: 'Assigned', value: 'assigned' },
              { count: unassignedTotal, label: 'Missing', value: 'unassigned' },
              ...(kind === 'group'
                ? [{ count: ungroupedTotal, label: 'No groups', value: 'ungrouped' as const }]
                : [])
            ]}
            layout="responsive"
            onChange={setStateFilter}
            value={state}
          />
        </div>

        <div className={styles.tableScroll}>
          <DataTableElement className={styles.table}>
            <thead>
              <tr>
                <th>Assigned</th>
                <th></th>
                <th>
                  <TableSortButton onClick={() => toggleSort('title')}>Track</TableSortButton>
                </th>
                <th>Current tags</th>
                <th>Groups</th>
                <th>
                  <TableSortButton onClick={() => toggleSort('duration')}>Duration</TableSortButton>
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((track) => {
                const pending = pendingTrackIds.includes(track.id)
                const isLoadingTrack = loadingTrackId === track.id
                const isPlayingTrack = playingTrackId === track.id
                const visibleLabels = track.labels.slice(0, maxVisibleAssignmentLabels)
                const hiddenLabelCount = Math.max(0, track.labels.length - maxVisibleAssignmentLabels)
                const labelNames = track.labels.map((label) => label.name).join(', ')
                const groupNames = track.groups.map((group) => group.name).join(', ')

                return (
                  <tr
                    className={cx(
                      styles.dropTarget,
                      dragOverTrackId === track.id ? styles.dropTargetActive : '',
                      selectedTrackId === track.id ? styles.dropTargetSelected : ''
                    )}
                    key={track.id}
                    onClick={() => setSelectedTrackId(track.id)}
                    onDragEnter={() => setDragOverTrackId(track.id)}
                    onDragLeave={() => setDragOverTrackId((current) => current === track.id ? null : current)}
                    onDragOver={(event) => {
                      if (!draggedTarget || pending) {
                        return
                      }

                      event.preventDefault()
                      event.dataTransfer.dropEffect = isTargetAssignedToTrack(track, draggedTarget) ? 'none' : 'copy'
                      setDragOverTrackId(track.id)
                    }}
                    onDrop={(event) => dropAssignmentOnTrack(event, track)}
                  >
                    <td data-label="Assigned">
                      <button
                        aria-label={`${track.assigned ? 'Remove' : 'Add'} ${targetName} for ${track.title}`}
                        className={cx(styles.toggle, track.assigned ? styles.toggleActive : '')}
                        disabled={pending}
                        onClick={() => void toggleBinding(track)}
                        type="button"
                      >
                        {track.assigned && <Check size={17} />}
                      </button>
                    </td>
                    <td className="col-icon" data-label="">
                      <div className="track-leading-control">
                        {track.status === 'ready' ? (
                          <IconButton
                            disabled={isLoadingTrack}
                            icon={isLoadingTrack ? <Loader2 size={15} /> : isPlayingTrack ? <Pause size={15} /> : <Play size={15} />}
                            iconSize="play"
                            label={isPlayingTrack ? `Pause ${track.title}` : `Play ${track.title}`}
                            loading={isLoadingTrack}
                            onClick={() => onPlayTrack(track)}
                            title={isPlayingTrack ? 'Pause' : 'Play'}
                          />
                        ) : (
                          <span className={`track-status-icon track-status-icon-${track.status}`} title={track.status}>
                            <Clock size={15} />
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="track-title-copy" data-label="Track">
                      <strong>{track.title}</strong>
                      <span>{track.artist || 'Unknown artist'} · {formatBpm(track.effective_bpm)}</span>
                    </td>
                    <td className={styles.secondaryCell} data-label="Current tags">
                      {track.labels.length > 0 ? (
                        <span className={styles.labelRow} aria-label={`Current tags for ${track.title}`} title={labelNames}>
                          {visibleLabels.map((label) => (
                            <span className={labelChipClassName(label.category)} key={`${label.category}-${label.id}`}>
                              {label.name}
                            </span>
                          ))}
                          {hiddenLabelCount > 0 && <span className={labelMoreClassName}>+{hiddenLabelCount}</span>}
                        </span>
                      ) : (
                        <span className="muted">No tags</span>
                      )}
                    </td>
                    <td className={styles.secondaryCell} data-label="Groups">
                      {groupNames || <span className="muted">No groups</span>}
                    </td>
                    <td data-label="Duration">{formatDuration(track.duration_seconds) ?? '-'}</td>
                  </tr>
                )
              })}
            </tbody>
          </DataTableElement>
          {items.length === 0 && <EmptyState message={loading ? 'Loading assignments...' : 'No tracks match these filters.'} />}
        </div>

        {pagination}
      </DataTableSurface>
    </div>
    <AudioAssignmentsMobileList
      activeLabels={activeLabels}
      assignedTotal={assignedTotal}
      groups={groups}
      items={items}
      kind={kind}
      loading={loading}
      loadingTrackId={loadingTrackId}
      onPlayTrack={onPlayTrack}
      onSearchInputChange={setSearchInput}
      onStateChange={setStateFilter}
      onTargetSelect={selectMobileTarget}
      onToggleBinding={(track) => void toggleBinding(track)}
      pendingTrackIds={pendingTrackIds}
      playingTrackId={playingTrackId}
      searchInput={searchInput}
      state={state}
      targetId={targetId}
      unassignedTotal={unassignedTotal}
      ungroupedTotal={ungroupedTotal}
    />
    <div className={styles.mobilePagination}>
      {pagination}
    </div>
    </>
  )
}
